import { fmtMoney, getLang, translate, useT, wordOf } from '../lib/i18n';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import * as api from '../lib/api';
import type { Booking, ClassItem, SessionType } from '../lib/api';
import { DayStrip } from '../components/DayStrip';
import { COACH_TZ, coachHour, fmtDayNum, fmtLongDay, fmtShortDay, fmtTime, fmtWeekday, nextDays, relativeTo, toCoachDateKey } from '../lib/time';
import { addLocalDays } from '../lib/local-days';
import { alert, confirm, haptic, inTelegram, useMainButton } from '../lib/telegram';

/**
 * Only until the coach's own horizon arrives with /api/me — the days a
 * client may book are the coach's to decide, not this file's.
 */
const HORIZON_DAYS = 30;

/**
 * More than this in one go is almost certainly a mis-tap, and the server
 * rate-limits writes at twenty a minute.
 */
const MAX_PICKS = 10;

/**
 * The slots endpoint answers at most 62 days in one request, on purpose
 * — nobody should be able to ask it for five years. A horizon longer
 * than that is therefore asked for in windows and stitched together
 * here, rather than by loosening the cap on the server.
 */
const SLOT_WINDOW_DAYS = 62;

async function loadSlots(typeId: string, days: Date[]) {
  const out: { startsAt: string; endsAt: string; hot?: boolean }[] = [];
  for (let i = 0; i < days.length; i += SLOT_WINDOW_DAYS) {
    const window = days.slice(i, i + SLOT_WINDOW_DAYS);
    const res = await api.getSlots(typeId, toCoachDateKey(window[0]),
      toCoachDateKey(window[window.length - 1]));
    out.push(...(res.slots ?? []));
  }
  return out;
}

/** A time the player has tapped, waiting to be booked with the rest. */
interface Pick { startsAt: string; endsAt: string; type: SessionType }

interface Props {
  credits: number;
  firstName: string;
  /** Has ever had a package: only then is the balance shown. */
  hadPackage: boolean;
  /** The credits pill in the header opens Credits on My profile. */
  onCredits: () => void;
  /** How far ahead this coach takes bookings. */
  horizonDays?: number;
  onChanged: () => void;
  /**
   * Credits are spent when the lesson happens (slice 2). A booking no
   * longer lowers the balance, so the balance alone cannot say what a
   * pick costs: the server walks it in among what is already booked.
   */
  creditOnUse?: boolean;
}

/** Weekly repeats worth offering, longest first once the short ones fit. */
const REPEATS = [1, 4, 8, 12];

/**
 * Which of them actually fit.
 *
 * Twelve weeks against a thirty-day horizon is eleven weeks the server
 * will refuse: it books what it can and reports the rest as skipped,
 * which reads as a half-failed booking. A count the coach cannot honour
 * is not a choice, so it is not shown — and if the horizon leaves room
 * for one session only, there is nothing to repeat and the whole row
 * goes.
 *
 * Counted from the session picked, not from today: a slot three weeks
 * out has three weeks less room than one tomorrow.
 */
function repeatsThatFit(firstStartsAt: string | null, horizonDays: number): number[] {
  if (!firstStartsAt) return REPEATS;
  const horizonEnd = Date.now() + horizonDays * 86_400_000;
  return REPEATS.filter((weeks) => {
    // Local wall-clock weeks, the way the series is actually generated.
    const last = new Date(firstStartsAt);
    last.setDate(last.getDate() + 7 * (weeks - 1));
    return last.getTime() <= horizonEnd;
  });
}

export function BookScreen({ credits, firstName, hadPackage, onCredits, onChanged, horizonDays = HORIZON_DAYS,
  creditOnUse = false }: Props) {
  const tr = useT();
  // As far ahead as this coach takes bookings, and no further: a strip
  // of thirty days under a ninety-day horizon hid two months the client
  // was allowed to book, and one of ninety under a thirty-day horizon
  // would offer days every tap is refused.
  const days = useMemo(() => nextDays(horizonDays), [horizonDays]);
  /**
   * «Записаться на среду, 09:00» from the bot opens the app at ?day=:
   * the same weekday the client has been coming on, already chosen, so
   * rebooking is two taps rather than a hunt through the strip.
   */
  const [selectedDay, setSelectedDay] = useState(() => {
    const asked = new URLSearchParams(window.location.search).get('day');
    return asked && /^\d{4}-\d{2}-\d{2}$/.test(asked) ? asked : toCoachDateKey(new Date());
  });
  // Until the player taps a day, the screen follows the nearest day with
  // free times: today when it has some, otherwise the next one that does.
  const [dayChosen, setDayChosen] = useState(false);
  const [next, setNext] = useState<Booking | null>(null);
  const [privateTypes, setPrivateTypes] = useState<SessionType[]>([]);
  const [privateType, setPrivateType] = useState<SessionType | null>(null);
  const [slots, setSlots] = useState<{ startsAt: string; endsAt: string; hot?: boolean }[]>([]);
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [picks, setPicks] = useState<Pick[]>([]);
  const [payWith, setPayWith] = useState<'credits' | 'cash'>('credits');
  const [repeatWeeks, setRepeatWeeks] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mainButton = useMainButton();

  // One pick behaves exactly as the single booking always did, weekly
  // repeat included. Two or more are booked one after another.
  const single = picks.length === 1 ? picks[0] : null;
  const pickedKeys = useMemo(() => new Set(picks.map((p) => p.startsAt)), [picks]);
  const pickedPerDay = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of picks) {
      const k = toCoachDateKey(new Date(p.startsAt));
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return m;
  }, [picks]);

  const unitOf = (t: SessionType) =>
    payWith === 'cash' && t.cash_price != null ? Number(t.cash_price) : Number(t.credit_cost);
  const repeatOptions = useMemo(
    () => repeatsThatFit(single?.startsAt ?? null, horizonDays),
    [single?.startsAt, horizonDays]);

  // Pick a later slot and twelve weeks may stop fitting; the choice
  // silently falls back to what does rather than booking a skipped series.
  useEffect(() => {
    if (!repeatOptions.includes(repeatWeeks)) setRepeatWeeks(repeatOptions[repeatOptions.length - 1] ?? 1);
  }, [repeatOptions, repeatWeeks]);

  const weeks = single ? repeatWeeks : 1;
  const total = picks.reduce((sum, p) => sum + unitOf(p.type), 0) * weeks;
  /**
   * With credits, as the server books it: each lesson takes every credit
   * left towards it and the rest is paid at the session (credits.ts,
   * splitSeat). 0.25 credit on an €80 lesson: 0.25 credit + €60. Never a
   * refusal, so no "not enough credits" any more.
   */
  const split = useMemo(() => {
    let left = credits, usedCredits = 0, cash = 0, covered = 0;
    const lessons = picks.flatMap((p) => Array.from({ length: weeks }, () => p.type));
    for (const t of lessons) {
      const cost = Number(t.credit_cost);
      const price = t.cash_price != null ? Number(t.cash_price) : null;
      const use = Math.max(0, Math.min(left, cost));
      left = Math.round((left - use) * 10000) / 10000;
      usedCredits += use;
      if (use < cost && price !== null) {
        const value = Math.round(use * (price / cost) * 100) / 100;
        covered += value;
        cash += Math.round((price - value) * 100) / 100;
      }
    }
    return { credits: Math.round(usedCredits * 10000) / 10000, cash, covered };
  }, [picks, weeks, credits]);
  const fmtCr = (n: number) => {
    const v = String(Math.round(n * 100) / 100);
    return getLang() === 'ru' ? v.replace('.', ',') : v;
  };

  /**
   * Under the new rule: each pick, every week of a repeat included, as the
   * coverage walk sees it — covered whole, or paid at the lesson. Asked
   * again whenever the picks change; a stale answer is dropped.
   */
  const lessonsPicked = useMemo(() => picks.flatMap((p) => Array.from({ length: weeks }, (_, w) => ({
    sessionTypeId: p.type.id,
    // The same local time each week, as the server books the series.
    startsAt: addLocalDays(new Date(p.startsAt), 7 * w, COACH_TZ).toISOString(),
    cost: Number(p.type.credit_cost),
  }))), [picks, weeks]);
  const [cover, setCover] = useState<Awaited<ReturnType<typeof api.coverPreview>> | null>(null);
  useEffect(() => {
    setCover(null);
    if (!creditOnUse || payWith !== 'credits' || lessonsPicked.length === 0) return;
    let gone = false;
    api.coverPreview(lessonsPicked.map(({ sessionTypeId, startsAt }) => ({ sessionTypeId, startsAt })))
      .then((r) => { if (!gone) setCover(r); })
      .catch(() => { /* no warning rather than a wrong one */ });
    return () => { gone = true; };
  }, [creditOnUse, payWith, lessonsPicked]);
  const uncovered = cover ? cover.lessons.filter((l) => !l.covered) : [];
  const uncoveredCash = uncovered.reduce((n, l) => n + Number(l.cashDue ?? 0), 0);
  const coveredCredits = cover
    ? cover.lessons.reduce((n, l, i) => n + (l.covered ? lessonsPicked[i]?.cost ?? 0 : 0), 0) : 0;

  const totalLabel = payWith === 'cash'
    ? fmtMoney(total)
    : creditOnUse
      ? (!cover ? wordOf(getLang(), 'book.creditTotal', total).replace('{n}', fmtCr(total))
        : uncovered.length === 0 ? wordOf(getLang(), 'book.creditTotal', coveredCredits).replace('{n}', fmtCr(coveredCredits))
        : coveredCredits > 0
          ? `${wordOf(getLang(), 'book.creditTotal', coveredCredits).replace('{n}', fmtCr(coveredCredits))} + ${fmtMoney(uncoveredCash)}`
          : fmtMoney(uncoveredCash))
    : split.cash > 0 && split.credits > 0
      ? `${wordOf(getLang(), 'book.creditTotal', split.credits).replace('{n}', fmtCr(split.credits))} + ${fmtMoney(split.cash)}`
      : split.cash > 0
        ? fmtMoney(split.cash)
        : wordOf(getLang(), 'book.creditTotal', total).replace('{n}', fmtCr(total));
  const mixedLengths = new Set(picks.map((p) => p.type.duration_min)).size > 1;

  /**
   * Tap to add, tap again to take it back. A pick that overlaps one
   * already chosen — a 90 at 18:00 against a 60 at 19:00 — replaces it,
   * since both can never be booked.
   */
  async function togglePick(slot: { startsAt: string; endsAt: string }) {
    if (!privateType) return;
    if (pickedKeys.has(slot.startsAt)) {
      setPicks((cur) => cur.filter((p) => p.startsAt !== slot.startsAt));
      return;
    }
    if (picks.length >= MAX_PICKS) {
      await alert(tr('book.maxPicks', { n: MAX_PICKS }));
      return;
    }
    const s0 = new Date(slot.startsAt).getTime();
    const e0 = new Date(slot.endsAt).getTime();
    setPicks((cur) => [
      ...cur.filter((p) => new Date(p.endsAt).getTime() <= s0 || new Date(p.startsAt).getTime() >= e0),
      { ...slot, type: privateType },
    ].sort((a, b) => a.startsAt.localeCompare(b.startsAt)));
  }

  /** The player's next booked session, for the header. */
  function loadNext() {
    api.getBookings(false)
      .then((rows) => setNext(rows.find((b) => b.status === 'booked') ?? null))
      .catch(() => { /* the header just says nothing is booked */ });
  }
  useEffect(() => { loadNext(); }, []);
  const changed = () => { onChanged(); loadNext(); };

  // Someone with no credits can only pay per session, so default there
  // rather than letting them pick an option that will fail.
  useEffect(() => {
    if (credits <= 0) setPayWith('cash');
  }, [credits]);

  // Two effects, not one. Previously a single effect both loaded the
  // catalogue and fetched slots — and because it called setPrivateType on
  // every run, choosing 90 minutes re-ran it and immediately reset the
  // choice back to the first type. Loading the catalogue once and fetching
  // slots separately removes that fight.

  // load the catalogue once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const types = await api.getSessionTypes();
        if (cancelled) return;
        const privs = types.filter((t) => t.kind === 'private');
        setPrivateTypes(privs);
        setPrivateType((current) => current ?? privs[0] ?? null);
        setClasses(await api.getClasses());
      } catch (err: any) {
        if (!cancelled) setError(readableError(err));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // slots depend on which length is chosen: a 90 needs a bigger gap, so
  // the available times genuinely differ
  useEffect(() => {
    if (!privateType) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const found = await loadSlots(privateType.id, days);
        if (cancelled) return;
        setSlots(found);
      } catch (err: any) {
        if (!cancelled) setError(readableError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [days, privateType]);

  const slotsByDay = useMemo(() => {
    const m = new Map<string, typeof slots>();
    for (const s of slots) {
      const k = toCoachDateKey(new Date(s.startsAt));
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(s);
    }
    return m;
  }, [slots]);

  const classesByDay = useMemo(() => {
    const m = new Map<string, ClassItem[]>();
    for (const c of classes) {
      const k = toCoachDateKey(new Date(c.starts_at));
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(c);
    }
    return m;
  }, [classes]);

  const emptyDays = useMemo(() => {
    const s = new Set<string>();
    for (const d of days) {
      const k = toCoachDateKey(d);
      if (!slotsByDay.get(k)?.length && !classesByDay.get(k)?.length) s.add(k);
    }
    return s;
  }, [days, slotsByDay, classesByDay]);

  // The first day with anything to book, for opening on it.
  const firstOpenDay = useMemo(
    () => days.map(toCoachDateKey).find((k) => !emptyDays.has(k)) ?? null,
    [days, emptyDays]);
  useEffect(() => {
    if (!dayChosen && !loading && firstOpenDay) setSelectedDay(firstOpenDay);
  }, [dayChosen, loading, firstOpenDay]);

  const dotDays = useMemo(
    () => new Set([...slotsByDay.keys()].filter((k) => slotsByDay.get(k)!.length > 0)),
    [slotsByDay]);

  /**
   * The chosen day's times as Morning (before 12:00), Afternoon (before
   * 17:00) and Evening, in the coach's time zone. Group classes sit among
   * the private times, in the part of the day they start in.
   */
  type DayItem = { kind: 'slot'; startsAt: string; endsAt: string; hot?: boolean } | { kind: 'class'; data: ClassItem };
  const dayParts = useMemo(() => {
    const items: DayItem[] = [
      ...(slotsByDay.get(selectedDay) ?? []).map((sl): DayItem => ({ kind: 'slot', ...sl })),
      ...(classesByDay.get(selectedDay) ?? []).map((c): DayItem => ({ kind: 'class', data: c })),
    ];
    const at = (x: DayItem) => (x.kind === 'slot' ? x.startsAt : x.data.starts_at);
    items.sort((a, b) => at(a).localeCompare(at(b)));
    const part = (x: DayItem) => { const h = coachHour(at(x)); return h < 12 ? 0 : h < 17 ? 1 : 2; };
    return (['book.morning', 'book.afternoon', 'book.evening'] as const)
      .map((key, i) => ({ key, items: items.filter((x) => part(x) === i) }))
      .filter((g) => g.items.length > 0);
  }, [slotsByDay, classesByDay, selectedDay]);

  // Telegram's MainButton is the confirm action — native position, native
  // styling, sits above the keyboard, and the user already knows what it is.
  const bookLabel = single
    ? repeatWeeks > 1
      ? tr('book.btnWeeks', { n: repeatWeeks, weeks: tr.word('book.weeks', repeatWeeks), total: totalLabel })
      : tr('book.btnOne', { total: totalLabel })
    : tr('book.btnMany', { n: picks.length, sessions: tr.word('book.session', picks.length), total: totalLabel });

  useEffect(() => {
    if (picks.length === 0) {
      mainButton.hide();
      return;
    }
    mainButton.show(bookLabel, bookAll);
    return () => mainButton.hide();
  }, [picks, payWith, repeatWeeks, bookLabel]);

  const [booking, setBooking] = useState(false);

  async function bookAll() {
    if (booking || picks.length === 0) return;
    setBooking(true);
    mainButton.busy();
    try {
      if (single) await bookOne(single);
      else await bookMany(picks);
    } finally {
      mainButton.idle();
      setBooking(false);
    }
  }

  async function bookOne(pick: Pick) {
    const type = pick.type;
    try {
      if (repeatWeeks > 1) {
        const res = await api.bookRecurring(type.id, pick.startsAt, repeatWeeks, payWith);
        haptic.success();
        for (const bk of res.booked) dropSlot(bk.startsAt, type.duration_min);
        setPicks([]);
        setRepeatWeeks(1);
        await refreshDay().catch(() => { /* stale list beats a false error */ });
        changed();
        // Partial success is normal, so say plainly which weeks took and
        // which didn't rather than a vague "some bookings failed".
        // "Already taken" is only true of a clash. A week past the
        // coach's horizon was not taken by anyone — saying so would
        // send them looking for a booking that does not exist.
        const clashes = res.skipped.filter((x) => x.reason !== 'beyond_horizon');
        const tooFar = res.skipped.filter((x) => x.reason === 'beyond_horizon');
        const skippedNote =
          (clashes.length
            ? tr('book.skippedTaken', {
                n: clashes.length, weeks: tr.word('book.weeks', clashes.length),
              })
            : '') +
          (tooFar.length
            ? tr('book.skippedHorizon', {
                n: tooFar.length, weeks: tr.word('book.weeks', tooFar.length),
              })
            : '');
        await alert(
          tr('book.booked', {
            n: res.booked.length, sessions: tr.word('book.session', res.booked.length),
          }) +
          (res.amountDue
            ? tr('book.totalCash', { amount: res.amountDue })
            : tr('book.creditsLeft', { n: res.creditsRemaining })) +
          skippedNote,
        );
        // The booking is done. Anything that fails from here — the
        // refresh, the balance reload — must not be reported as a
        // failed booking, which is what sent an error alert after 20
        // sessions had been successfully created.
        return;
      }
      const res = await api.bookPrivate(type.id, pick.startsAt, payWith);
      haptic.success();
      dropSlot(pick.startsAt, type.duration_min);
      setPicks([]);
      await refreshDay().catch(() => { /* stale list beats a false error */ });
      changed();
      await alert(
        res.amountDue && res.creditValue
          ? tr('book.doneSplit', { cash: Math.round(res.amountDue), covered: Math.round(res.creditValue) })
          : res.amountDue
          ? tr('book.doneOne', { amount: res.amountDue })
          : tr('book.doneOneCredits', { n: res.creditsRemaining, credits: tr.word('book.credit', res.creditsRemaining) }),
      );
    } catch (err: any) {
      haptic.error();
      await alert(readableError(err));
      if (err?.code === 'slot_taken' || err?.code === 'slot_unavailable') refreshDay();
    }
  }

  /**
   * Several different times, booked one after another.
   *
   * Each is its own booking, exactly as if they had been tapped through
   * one at a time, so one that fails — taken a moment ago, out of
   * credits — does not undo the rest. What did not book stays in the
   * tray when it is still worth retrying (switch to pay per session and
   * go again); a slot someone else took is dropped, since it is gone.
   */
  async function bookMany(list: Pick[]) {
    const booked: Pick[] = [];
    const failed: { pick: Pick; err: any }[] = [];
    let lastRes: any = null;
    for (const pick of list) {
      try {
        lastRes = await api.bookPrivate(pick.type.id, pick.startsAt, payWith);
        booked.push(pick);
        dropSlot(pick.startsAt, pick.type.duration_min);
      } catch (err: any) {
        failed.push({ pick, err });
      }
    }

    const gone = (e: any) => e?.code === 'slot_taken' || e?.code === 'slot_unavailable';
    setPicks(failed.filter((f) => !gone(f.err)).map((f) => f.pick));
    if (booked.length) haptic.success(); else haptic.error();
    await refreshDay().catch(() => { /* stale list beats a false error */ });
    if (booked.length) changed();

    const when = (p: Pick) => `${fmtLongDay(new Date(p.startsAt))} ${fmtTime(p.startsAt)}`;
    const head = booked.length === list.length
      ? tr('book.doneAll', { n: booked.length })
      : booked.length
        ? tr('book.doneSome', { n: booked.length, total: list.length })
        : tr('book.doneNone');
    const money = booked.length && lastRes
      ? payWith === 'cash' || lastRes.amountDue
        ? tr('book.payEach')
        : tr('book.creditsLeftSuffix', { n: lastRes.creditsRemaining, credits: tr.word('book.credit', lastRes.creditsRemaining) })
      : '';
    const why = failed.map((f) => `• ${when(f.pick)}: ${readableError(f.err)}`).join('\n');
    await alert(head + money + (why ? `\n\n${why}` : ''));
  }

  /**
   * Reload after a successful action, swallowing any failure.
   *
   * The booking has already happened. If reloading the slot list fails —
   * flaky connection, a slow database wake-up — that is not a failed
   * booking, and telling the client it was is worse than a stale list.
   */
  /**
   * Take the booked slot off the list straight away.
   *
   * Waiting for a server round trip leaves the old list on screen, so the
   * next tap hits a time that's just been taken and shows "no longer
   * open" — even though the booking itself worked. Removing it locally
   * keeps the screen honest between refreshes.
   *
   * A 90-minute booking also blocks the hour after it, so anything
   * overlapping goes too.
   */
  function dropSlot(startsAt: string, durationMin: number) {
    const from = new Date(startsAt).getTime();
    const to = from + durationMin * 60000;
    setSlots((current) =>
      current.filter((sl) => {
        const s1 = new Date(sl.startsAt).getTime();
        const e1 = new Date(sl.endsAt).getTime();
        return e1 <= from || s1 >= to;
      }),
    );
  }

  function refreshQuietly() {
    void Promise.resolve()
      .then(() => onChanged())
      .then(() => refreshDay())
      .catch(() => { /* stale list, not a failed booking */ });
  }

  async function refreshDay() {
    if (!privateType) return;
    const [found, classRes] = await Promise.all([
      loadSlots(privateType.id, days),
      api.getClasses(),
    ]);
    setSlots(found);
    setClasses(classRes);
  }

  async function joinClass(c: ClassItem) {
    const full = c.seats_left <= 0;
    // New rule: a class the package does not reach says so before the seat
    // is taken (§2.8), and what it costs instead.
    let warn = '';
    if (!full && creditOnUse) {
      const r = await api.coverPreview([{ sessionId: c.id }]).catch(() => null);
      const l = r?.lessons[0];
      if (l && !l.covered && l.cashDue != null) warn = `\n\n${tr('book.notCovered', { amount: fmtMoney(l.cashDue) })}`;
    }
    const ok = await confirm(
      full
        ? tr('book.classFull', { title: c.title })
        // The class title is the coach's own wording and stays as typed.
        : tr('book.joinClass', {
            title: c.title, time: fmtTime(c.starts_at), n: c.credit_cost,
            credits: tr.word('book.credit', c.credit_cost),
          }) + warn,
    );
    if (!ok) return;

    try {
      const res = await api.bookClass(c.id);
      haptic.success();
      await alert(
        res.status === 'waitlisted'
          ? tr('book.waitlisted', { n: res.waitlistPosition ?? 0 })
          : tr('book.seeYouThere'),
      );
      changed();
      refreshDay();
    } catch (err: any) {
      haptic.error();
      await alert(readableError(err));
      refreshDay();
    }
  }

  const canPayCredits = credits > 0;
  const showPay = canPayCredits && privateType?.cash_price != null;
  const priceOf = (t: SessionType) => t.cash_price != null
    ? fmtMoney(t.cash_price)
    : tr.word('rail.creditCost', Number(t.credit_cost)).replace('{n}', String(t.credit_cost));
  const PART_ICONS = [SunIcon, MiddayIcon, MoonIcon];

  return (
    <>
      {/* Who it is for and what they already have: the next session, like the coach's Next up. */}
      <div className="bhead">
        <div className="bhead__top">
          <span className="bhead__hi">{tr('book.hi', { name: firstName })}</span>
          {hadPackage && (
            <button className="bhead__credits" onClick={() => { haptic.tap(); onCredits(); }}>
              {tr('lv.nCredits', { n: credits, credits: tr.word('credits.word', credits) })}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
            </button>
          )}
        </div>
        {next ? (
          <>
            <div className="bhead__row">
              <span className="bhead__label">{tr('book.nextUp')}</span>
              <span className="bhead__in">{tr('bookings.in', { time: relativeTo(next.starts_at) })}</span>
            </div>
            <div className="bhead__time">{fmtTime(next.starts_at)}<small>– {fmtTime(next.ends_at)}</small></div>
            <div className="bhead__what">
              <b>{fmtLongDay(new Date(next.starts_at))}</b>
              <span> · {next.kind === 'private'
                ? tr('bookings.private', { min: Math.round((new Date(next.ends_at).getTime() - new Date(next.starts_at).getTime()) / 60_000) })
                : next.title}</span>
            </div>
          </>
        ) : (
          <div className="bhead__none">{tr('book.nothingYet')}</div>
        )}
      </div>

      {privateTypes.length > 1 && (
        <div className="bseg" role="radiogroup" aria-label={tr('book.sessionLength')}>
          {privateTypes.map((t) => (
            <button key={t.id} role="radio" aria-checked={privateType?.id === t.id}
              onClick={() => { haptic.tap(); setPrivateType(t); }}>
              {tr('book.minShort', { n: t.duration_min })}
              <small>{priceOf(t)}</small>
            </button>
          ))}
        </div>
      )}

      <DayStrip
        days={days}
        selected={selectedDay}
        emptyDays={emptyDays}
        dotDays={dotDays}
        pickedPerDay={pickedPerDay}
        followSelected
        onSelect={(k) => {
          haptic.tap();
          setDayChosen(true);
          setSelectedDay(k);
        }}
      />

      {error && <div className="error-bar">{error}</div>}

      <div className={`btimes${picks.length > 0 ? ' btimes--picked' : ''}`}>
        <div className="btimes__day">{fmtLongDay(new Date(`${selectedDay}T12:00:00`))}</div>
        {loading ? (
          <>
            <div className="skeleton" />
            <div className="skeleton" />
          </>
        ) : dayParts.length === 0 ? (
          <div className="empty">
            <div className="empty__lead">{tr('rail.nothingOpen')}</div>
            {tr('rail.tryAnother')}
          </div>
        ) : (
          <>
            {dayParts.map((g) => {
              const Icon = PART_ICONS[['book.morning', 'book.afternoon', 'book.evening'].indexOf(g.key)];
              return (
                <div className="bpart" key={g.key}>
                  <div className="bpart__label"><Icon />{tr(g.key)}</div>
                  <div className="bchips">
                    {g.items.map((x) => x.kind === 'slot' ? (
                      // 🔥: the coach marked this time as one they want filled.
                      <button key={x.startsAt} className={`bchip${x.hot ? ' bchip--hot' : ''}`} aria-pressed={pickedKeys.has(x.startsAt)}
                        onClick={() => { haptic.press(); void togglePick(x); }}>
                        {x.hot && <span aria-hidden="true">🔥</span>}
                        {fmtTime(x.startsAt)}
                      </button>
                    ) : (
                      // The class title is the coach's own wording and stays as typed.
                      <button key={x.data.id} className="bchip bchip--class" disabled={x.data.my_status !== null}
                        onClick={() => joinClass(x.data)}>
                        {fmtTime(x.data.starts_at)}
                        <small>
                          {x.data.title} · {x.data.my_status === 'booked' ? tr('rail.booked')
                            : x.data.my_status === 'waitlisted' ? tr('rail.waiting')
                            : x.data.seats_left > 0 ? tr('book.seatsShort', { n: x.data.seats_left })
                            : tr('book.classFullShort')}
                        </small>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
            {picks.length === 0 && <div className="btip">{tr('book.tip')}</div>}
          </>
        )}
      </div>

      {/* What is picked, how to pay, and the button: kept at the bottom while scrolling. */}
      {picks.length > 0 && (
        <div className="bbar">
          {showPay && (
            <div className="bbar__pay">
              <span className="bbar__label">{tr('book.payWith')}</span>
              <div className="bseg bseg--small">
                <button aria-checked={payWith === 'credits'} role="radio"
                  onClick={() => { haptic.tap(); setPayWith('credits'); }}>
                  {tr('book.payCredits')} <small>· {tr('book.nLeft', { n: credits })}</small>
                </button>
                <button aria-checked={payWith === 'cash'} role="radio"
                  onClick={() => { haptic.tap(); setPayWith('cash'); }}>
                  {tr('book.payCash')} <small>· {fmtMoney(privateType!.cash_price!)}
                    {(privateType as any).custom_rate ? tr('book.yourRate') : ''}</small>
                </button>
              </div>
            </div>
          )}
          {single && repeatOptions.length > 1 && (
            <div className="bbar__repeat">
              <span className="bbar__label">{tr('book.trainEveryWeek')}</span>
              {repeatOptions.map((w) => (
                <button key={w} aria-pressed={repeatWeeks === w}
                  onClick={() => { haptic.tap(); setRepeatWeeks(w); }}>
                  {w === 1 ? tr('book.justOnce') : `× ${w}`}
                </button>
              ))}
            </div>
          )}
          {single ? (
            <div className="bbar__summary">
              {fmtShortDay(new Date(single.startsAt))} · {fmtTime(single.startsAt)} · {tr('book.minShort', { n: single.type.duration_min })}
            </div>
          ) : (
            <div className="bbar__picks">
              {picks.map((p) => (
                <button key={p.startsAt} className="tray__pick"
                  onClick={() => {
                    haptic.tap();
                    setDayChosen(true);
                    setSelectedDay(toCoachDateKey(new Date(p.startsAt)));
                  }}>
                  <span className="tray__day">{fmtWeekday(new Date(p.startsAt))} {fmtDayNum(new Date(p.startsAt))}</span>
                  <b>{fmtTime(p.startsAt)}</b>
                  {mixedLengths && <small>{p.type.duration_min}′</small>}
                  <span className="tray__x" role="button" aria-label={tr('a11y.remove')}
                    onClick={(e) => {
                      e.stopPropagation();
                      haptic.tap();
                      setPicks((cur) => cur.filter((x) => x.startsAt !== p.startsAt));
                    }}>×</span>
                </button>
              ))}
            </div>
          )}
          {/* New rule: what the package does not reach, said before confirming (§2.8). */}
          {creditOnUse && payWith === 'credits' && uncovered.length > 0 && (
            <div className="bwarn">
              {lessonsPicked.length === 1
                ? tr('book.notCovered', { amount: fmtMoney(uncoveredCash) })
                : tr('book.notCoveredN', { n: uncovered.length, total: lessonsPicked.length, amount: fmtMoney(uncoveredCash) })}
            </div>
          )}
          {creditOnUse && payWith === 'credits' && cover && cover.displaced.length > 0 && (
            <div className="bwarn">
              {cover.displaced.length === 1
                ? tr('book.displaces', {
                    day: `${fmtShortDay(new Date(cover.displaced[0].startsAt))}, ${fmtTime(cover.displaced[0].startsAt)}`,
                    amount: fmtMoney(Number(cover.displaced[0].cashDue ?? 0)),
                  })
                : tr('book.displacesN', {
                    n: cover.displaced.length,
                    amount: fmtMoney(cover.displaced.reduce((n, d) => n + Number(d.cashDue ?? 0), 0)),
                  })}
            </div>
          )}
          {/* Partly in credits: what is left to pay, and what the credits cover. */}
          {!creditOnUse && payWith === 'credits' && split.cash > 0 && split.credits > 0 && (
            <div className="bsplit">
              {tr('book.splitLine', { cash: Math.round(split.cash), covered: Math.round(split.covered) })}
            </div>
          )}
          {/* Inside Telegram the button is Telegram's own, just below this bar. */}
          {!inTelegram && (
            <button className="savebtn savebtn--big" disabled={booking} onClick={bookAll}>
              {booking ? tr('book.booking') : bookLabel}
            </button>
          )}
        </div>
      )}
    </>
  );
}

const SunIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);
const MiddayIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="14" r="4" /><path d="M3 18h18M12 4v3M5.6 8.6l1.4 1.4M18.4 8.6L17 10" />
  </svg>
);
const MoonIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" />
  </svg>
);

/**
 * Errors say what happened and what to do about it. "insufficient_credits"
 * is not a message; "You're out of credits" plus a way forward is.
 */
/**
 * What someone sees on Book before their coach has approved them: no
 * times, and the one thing worth knowing — a message will come.
 */
/** Book, while the coach is switched off: nothing new until they are back on. */
export function BookingClosed() {
  const t = useT();
  return (
    <div className="bempty">
      <div className="bempty__icon">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3.5" y="5" width="17" height="15.5" rx="3" /><path d="M3.5 10h17M8 3v4M16 3v4" />
        </svg>
      </div>
      <div className="bempty__lead">{t('book.closedTitle')}</div>
      <div className="bempty__hint">{t('book.closedBody')}</div>
    </div>
  );
}

export function WaitingForApproval({ coachName }: { coachName: string | null }) {
  const tr = useT();
  return (
    <div className="waiting">
      <span className="waiting__icon" aria-hidden="true">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
          strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      </span>
      <div className="waiting__title">{tr('book.waitTitle')}</div>
      <div className="waiting__body">{tr('book.waitBody', { coach: coachName ?? tr('book.yourCoach') })}</div>
    </div>
  );
}

export function readableError(err: any): string {
  switch (err?.code) {
    // Reused or too old: Telegram gives fresh sign-in data only on a new open.
    case 'init_data_reused':
    case 'init_data_stale':
      return translate(getLang(), 'err.reopenApp');
    // A start link that matches no coach: nobody is filed anywhere.
    case 'unknown_link':
      return translate(getLang(), 'err.unknownLink');
    case 'slot_taken':
      return translate(getLang(), 'err.slotTaken');
    case 'slot_unavailable':
      return translate(getLang(), 'err.slotGone');
    case 'insufficient_credits':
      return err.detail?.cashPrice
        ? translate(getLang(), 'err.noCredits', { price: err.detail.cashPrice })
        : translate(getLang(), 'err.noCreditsPlain');
    case 'class_full':
      return translate(getLang(), 'err.classFull');
    case 'already_booked':
      return translate(getLang(), 'err.alreadyInClass');
    case 'too_soon':
      return translate(getLang(), 'err.tooSoon', { hours: err.detail?.minNoticeHours ?? 2 });
    case 'too_far_ahead':
      return translate(getLang(), 'err.tooFar', { days: err.detail?.horizonDays ?? 30 });
    case 'already_cancelled':
      return translate(getLang(), 'err.alreadyCancelled');
    case 'lesson_started':
      return translate(getLang(), 'err.lessonStarted');
    case 'too_many_requests':
      return translate(getLang(), 'err.rateLimited');
    case 'invalid_init_data':
      return translate(getLang(), 'err.signIn');
    case 'blocked':
      return translate(getLang(), 'err.blocked');
    case 'not_approved':
      return translate(getLang(), 'err.notApproved');
    case 'coach_off':
      return translate(getLang(), 'book.closedBody');
    default:
      return translate(getLang(), 'err.tryAgain');
  }
}
